import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Grid,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

import {
  SaveEquipment,
  UpdateEquipment,
} from "../../../services/GeneralMaintenanceService";
import ThermometerPickerDialog from "./ThermometerPickerDialog";

import { NotificationManager } from "react-notifications";

const AddEditEquipmentDialog = ({
  open,
  onClose,
  onSave,
  selectedEquipment,
}) => {
  // ================= BASIC =================
  const [item, setItem] = useState("");
  const [description, setDescription] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [equipmentType, setEquipmentType] = useState("");
  const [assetId, setAssetId] = useState("");

  // ================= DATES =================
  const [dateReceived, setDateReceived] = useState("");
  const [dateCommissioned, setDateCommissioned] = useState("");
  const [warrantyExpiration, setWarrantyExpiration] = useState("");

  // ================= FINANCIAL =================
  const [purchasePrice, setPurchasePrice] = useState("");
  const [currentValue, setCurrentValue] = useState("");
  const [salvageValue, setSalvageValue] = useState("");
  const [depreciationLife, setDepreciationLife] = useState("");
  const [age, setAge] = useState("");

  // ================= WARRANTY / STATUS =================
  const [vendor, setVendor] = useState("");
  const [warrantyStatus, setWarrantyStatus] = useState("");
  const [equipmentStatus, setEquipmentStatus] = useState("");
  const [location, setLocation] = useState("");

  // ================= CALIBRATION =================
  const [calibrationServiceProvider, setCalibrationServiceProvider] = useState("");
  const [analyst, setAnalyst] = useState("");
  const [dateCalibrated, setDateCalibrated] = useState("");
  const [expirationDate, setExpirationDate] = useState("");

  const [thermometer1, setThermometer1] = useState("");
  const [thermometer2, setThermometer2] = useState("");
  const [thermometer3, setThermometer3] = useState("");

  const [lowTemp, setLowTemp] = useState("");
  const [highTemp, setHighTemp] = useState("");

  // ================= THERMOMETER PICKER =================
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState(null); // 1 | 2 | 3

  const openPicker = (slot) => {
    setPickerTarget(slot);
    setPickerOpen(true);
  };

  const handlePickerSelect = (serialNumber) => {
    const serial = (serialNumber || "").trim();
    if (pickerTarget === 1) setThermometer1(serial);
    else if (pickerTarget === 2) setThermometer2(serial);
    else if (pickerTarget === 3) setThermometer3(serial);
    setPickerOpen(false);
  };

  // ================= OTHER =================
  const [equipmentManual, setEquipmentManual] = useState("");
  const [validationRecords, setValidationRecords] = useState("");
  const [saving, setSaving] = useState(false);

  const [itemId, setItemId] = useState(0);
  const [recordid, setRecordid] = useState(0);

  const formatDate = (d) => (d ? d.split("T")[0] : "");

  // ================= LOAD EDIT =================
  useEffect(() => {
    if (!selectedEquipment) return;

    setItem(selectedEquipment.item || "");
    setDescription(selectedEquipment.description || "");
    setMake(selectedEquipment.make || "");
    setModel(selectedEquipment.model || "");
    setSerialNumber(selectedEquipment.serialNumber || "");
    setEquipmentType(selectedEquipment.equipmentType || "");
    setAssetId(selectedEquipment.assetId || "");

    setDateReceived(formatDate(selectedEquipment.dateReceived));
    setDateCommissioned(formatDate(selectedEquipment.dateCommissioned));
    setWarrantyExpiration(formatDate(selectedEquipment.warrantyExpiration));

    setPurchasePrice(selectedEquipment.purchasePrice || "");
    setCurrentValue(selectedEquipment.currentValue || "");
    setSalvageValue(selectedEquipment.salvageValue || "");
    setDepreciationLife(selectedEquipment.depreciationLife || "");
    setAge(selectedEquipment.age || "");

    setVendor(selectedEquipment.vendor || "");
    setWarrantyStatus(selectedEquipment.warrantyStatus || "");
    setEquipmentStatus(selectedEquipment.equipmentStatus || "");
    setLocation(selectedEquipment.location || "");

    setCalibrationServiceProvider(
      selectedEquipment.calibrationServiceProvider || ""
    );
    setAnalyst(selectedEquipment.analyst || "");
    setDateCalibrated(formatDate(selectedEquipment.dateCalibrated));
    setExpirationDate(formatDate(selectedEquipment.expirationDate));

    // Trim on load too — rows saved before this fix may already hold padded values
    setThermometer1((selectedEquipment.thermometer1 || "").trim());
    setThermometer2((selectedEquipment.thermometer2 || "").trim());
    setThermometer3((selectedEquipment.thermometer3 || "").trim());

    setLowTemp(selectedEquipment.lowTemp || "");
    setHighTemp(selectedEquipment.highTemp || "");

    setEquipmentManual(selectedEquipment.equipmentManual || "");
    setValidationRecords(selectedEquipment.validationRecords || "");

    setItemId(selectedEquipment.itemId || 0);
    setRecordid(selectedEquipment.recordid || 0);
  }, [selectedEquipment]);

  // ================= SAVE =================
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        item,
        description,
        make,
        model,
        serialNumber,
        equipmentType,
        assetId: assetId ? parseInt(assetId) : null,

        dateReceived: dateReceived || null,
        dateCommissioned: dateCommissioned || null,
        warrantyExpiration: warrantyExpiration || null,

        purchasePrice: purchasePrice ? parseFloat(purchasePrice) : null,
        currentValue: currentValue ? parseFloat(currentValue) : null,
        salvageValue: salvageValue ? parseFloat(salvageValue) : null,
        depreciationLife: depreciationLife ? parseInt(depreciationLife) : null,
        age: age ? parseInt(age) : null,

        vendor,
        warrantyStatus,
        equipmentStatus,
        location,

        calibrationServiceProvider,
        analyst,
        dateCalibrated: dateCalibrated || null,
        expirationDate: expirationDate || null,

        thermometer1: thermometer1.trim(),
        thermometer2: thermometer2.trim(),
        thermometer3: thermometer3.trim(),

        lowTemp: lowTemp ? parseFloat(lowTemp) : null,
        highTemp: highTemp ? parseFloat(highTemp) : null,

        equipmentManual,
        validationRecords,

        itemId,
        recordid,
      };

      let response;

      if (selectedEquipment) {
        response = await UpdateEquipment(payload);
        NotificationManager.success("Updated successfully");
      } else {
        response = await SaveEquipment(payload);
        NotificationManager.success("Saved successfully");
      }

      if (response?.status === 200) {
        onSave();
        onClose();
      } else {
        NotificationManager.error("Operation failed");
      }
    } catch (err) {
      NotificationManager.error("Error saving equipment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        {selectedEquipment ? "Edit Equipment" : "Add Equipment"}
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={1.5}>

          {/* ================= BASIC ================= */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" label="Item" fullWidth value={item}
              onChange={(e) => setItem(e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" label="Description" fullWidth value={description}
              onChange={(e) => setDescription(e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" label="Make" fullWidth value={make}
              onChange={(e) => setMake(e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" label="Model" fullWidth value={model}
              onChange={(e) => setModel(e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" label="Serial Number" fullWidth value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl size="small" fullWidth>
              <InputLabel>Equipment Type</InputLabel>
              <Select
                label="Equipment Type"
                value={equipmentType}
                onChange={(e) => setEquipmentType(e.target.value)}
              >
                <MenuItem value="">— None —</MenuItem>
                <MenuItem value="Refrigerator">Refrigerator</MenuItem>
                <MenuItem value="Freezer">Freezer</MenuItem>
                <MenuItem value="Thermometer">Thermometer</MenuItem>
                <MenuItem value="Incubator">Incubator</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" label="Asset ID" fullWidth value={assetId}
              onChange={(e) => setAssetId(e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" label="Location" fullWidth value={location}
              onChange={(e) => setLocation(e.target.value)} />
          </Grid>

          {/* ================= DATES ================= */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" type="date" label="Received"
              InputLabelProps={{ shrink: true }} fullWidth value={dateReceived}
              onChange={(e) => setDateReceived(e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" type="date" label="Commissioned"
              InputLabelProps={{ shrink: true }} fullWidth value={dateCommissioned}
              onChange={(e) => setDateCommissioned(e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" type="date" label="Warranty Expiration"
              InputLabelProps={{ shrink: true }} fullWidth value={warrantyExpiration}
              onChange={(e) => setWarrantyExpiration(e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" type="date" label="Calibrated Date"
              InputLabelProps={{ shrink: true }} fullWidth value={dateCalibrated}
              onChange={(e) => setDateCalibrated(e.target.value)} />
          </Grid>

          {/* ================= CALIBRATION ================= */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              size="small" label="Thermometer 1" fullWidth value={thermometer1}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    {thermometer1 && (
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setThermometer1(""); }}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => openPicker(1)}>
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onClick={() => openPicker(1)}
              sx={{ cursor: "pointer" }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              size="small" label="Thermometer 2" fullWidth value={thermometer2}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    {thermometer2 && (
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setThermometer2(""); }}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => openPicker(2)}>
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onClick={() => openPicker(2)}
              sx={{ cursor: "pointer" }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              size="small" label="Thermometer 3" fullWidth value={thermometer3}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    {thermometer3 && (
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setThermometer3(""); }}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => openPicker(3)}>
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onClick={() => openPicker(3)}
              sx={{ cursor: "pointer" }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" label="Low Temp" type="number" fullWidth value={lowTemp}
              onChange={(e) => setLowTemp(e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" label="High Temp" type="number" fullWidth value={highTemp}
              onChange={(e) => setHighTemp(e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" label="Analyst" fullWidth value={analyst}
              onChange={(e) => setAnalyst(e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" label="Calibration Provider" fullWidth value={calibrationServiceProvider}
              onChange={(e) => setCalibrationServiceProvider(e.target.value)} />
          </Grid>

          {/* ================= FINANCIAL ================= */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" label="Purchase Price" type="number" fullWidth value={purchasePrice}
              onChange={(e) => setPurchasePrice(e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" label="Current Value" type="number" fullWidth value={currentValue}
              onChange={(e) => setCurrentValue(e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" label="Salvage Value" type="number" fullWidth value={salvageValue}
              onChange={(e) => setSalvageValue(e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField size="small" label="Depreciation Life" type="number" fullWidth value={depreciationLife}
              onChange={(e) => setDepreciationLife(e.target.value)} />
          </Grid>

        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? "Saving..." : selectedEquipment ? "Update" : "Save"}
        </Button>
      </DialogActions>

      <ThermometerPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handlePickerSelect}
        excludeItemId={itemId}
      />
    </Dialog>
  );
};

export default AddEditEquipmentDialog;